import React, { useState, useCallback } from 'react';
import { View, Pressable, Platform } from 'react-native';
import { Text } from '@constants/Themed';
import { YStack, XStack, H6, SizableText, Button, Spinner } from 'tamagui';
import { MaterialCommunityIcons, FontAwesome } from '@expo/vector-icons';
import apis from '@api/api';
import { useDispatch } from 'react-redux';
import { SHOW_AUTH_MODAL } from '@reduxLocal/actions/types';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import * as Animatable from 'react-native-animatable';
import styles from '@stylesheet';
import { getModernCardStyle, getModernTextColor } from '@stylesheet';

interface DatabaseConnectionCardProps {
  connection: {
    _id: string;
    name: string;
    type: 'postgresql' | 'mysql' | 'mongodb' | 'sqlite';
    host: string;
    port: number;
    database: string;
    status: 'active' | 'inactive' | 'error';
    tags?: string[];
    lastTestedAt?: string;
    errorMessage?: string;
  };
  onEdit?: (connection: any) => void;
  onDelete?: (connectionId: string) => void;
  onImportSchema?: (connectionId: string) => void;
  onTest?: (connectionId: string) => void;
}

const DatabaseConnectionCard: React.FC<DatabaseConnectionCardProps> = ({
  connection,
  onEdit,
  onDelete,
  onImportSchema,
  onTest
}) => {
  const dispatch = useDispatch();
  const theme = themeSelector();
  const [testing, setTesting] = useState(false);
  const cardStyle = getModernCardStyle(theme, 'medium');

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'postgresql':
        return 'database';
      case 'mysql':
        return 'database';
      case 'mongodb':
        return 'database';
      case 'sqlite':
        return 'database';
      default:
        return 'database';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return '#10b981';
      case 'error':
        return '#ef4444';
      default:
        return '#6b7280';
    }
  };

  const handleTest = useCallback(async () => {
    if (testing) return;
    
    setTesting(true);
    try {
      const result = await apis.testDatabaseConnection(connection._id);
      if (onTest) {
        onTest(connection._id);
      }
    } catch (error) {
      console.error('Error testing connection:', error);
    } finally {
      setTesting(false);
    }
  }, [connection._id, testing, onTest]);

  const handleDelete = useCallback(async () => {
    if (onDelete) {
      onDelete(connection._id);
    }
  }, [connection._id, onDelete]);

  return (
    <Animatable.View
      animation="fadeInUp"
      duration={300}
      style={{ marginBottom: 16 }}
    >
      <Pressable
        style={[
          cardStyle,
          {
            flex: 1,
            minHeight: 200,
            borderWidth: 2,
            borderColor: connection.status === 'active' 
              ? 'rgba(16, 185, 129, 0.3)' 
              : connection.status === 'error'
              ? 'rgba(239, 68, 68, 0.3)'
              : 'rgba(0, 0, 0, 0.06)',
            ...(Platform.OS === 'web' && {
              transition: 'all 0.3s ease',
            }),
          }
        ]}
      >
        <YStack flex={1} gap="$2" padding="$3">
          {/* Header */}
          <XStack justifyContent="space-between" alignItems="flex-start" gap="$2">
            <XStack flex={1} alignItems="center" gap="$2">
              <View style={{
                width: 40,
                height: 40,
                backgroundColor: theme ? '#eff6ff' : '#1e3a8a',
                borderRadius: 12,
                justifyContent: 'center',
                alignItems: 'center',
                borderWidth: 2,
                borderColor: theme ? '#e2e8f0' : '#334155',
              }}>
                <MaterialCommunityIcons
                  name={getTypeIcon(connection.type)}
                  size={20}
                  color={theme ? '#3b82f6' : '#60a5fa'}
                />
              </View>
              <YStack flex={1}>
                <H6
                  color={getModernTextColor(theme, 'primary')}
                  fontWeight="600"
                  numberOfLines={2}
                >
                  {connection.name}
                </H6>
                <SizableText
                  size="$2"
                  color={getModernTextColor(theme, 'muted')}
                >
                  {connection.type.toUpperCase()}
                </SizableText>
              </YStack>
            </XStack>
            
            {/* Status Indicator */}
            <XStack alignItems="center" gap="$1">
              <View style={{
                width: 8,
                height: 8,
                borderRadius: 4,
                backgroundColor: getStatusColor(connection.status)
              }} />
              <SizableText
                size="$1"
                color={getStatusColor(connection.status)}
                fontWeight="600"
              >
                {connection.status}
              </SizableText>
            </XStack>
          </XStack>

          {/* Connection Details */}
          <YStack gap="$1" marginTop="$2">
            <XStack alignItems="center" gap="$2">
              <MaterialCommunityIcons
                name="server"
                size={14}
                color={getModernTextColor(theme, 'muted')}
              />
              <SizableText
                size="$2"
                color={getModernTextColor(theme, 'muted')}
              >
                {connection.host}:{connection.port}
              </SizableText>
            </XStack>
            <XStack alignItems="center" gap="$2">
              <MaterialCommunityIcons
                name="database"
                size={14}
                color={getModernTextColor(theme, 'muted')}
              />
              <SizableText
                size="$2"
                color={getModernTextColor(theme, 'muted')}
              >
                {connection.database}
              </SizableText>
            </XStack>
          </YStack>

          {/* Tags */}
          {connection.tags && connection.tags.length > 0 && (
            <XStack flexWrap="wrap" gap="$1" marginTop="$2">
              {connection.tags.map((tag, index) => (
                <View
                  key={index}
                  style={{
                    backgroundColor: theme ? '#f1f5f9' : '#334155',
                    paddingHorizontal: 8,
                    paddingVertical: 4,
                    borderRadius: 8,
                  }}
                >
                  <SizableText
                    size="$1"
                    color={getModernTextColor(theme, 'secondary')}
                  >
                    {tag}
                  </SizableText>
                </View>
              ))}
            </XStack>
          )}

          {/* Actions */}
          <XStack gap="$2" marginTop="auto" paddingTop="$3">
            <Button
              size="$2"
              onPress={handleTest}
              disabled={testing}
              backgroundColor={theme ? '#3b82f6' : '#2563eb'}
              color="white"
              flex={1}
            >
              {testing ? (
                <Spinner size="small" color="white" />
              ) : (
                <SizableText color="white" size="$2">
                  Test
                </SizableText>
              )}
            </Button>
            
            {onImportSchema && (
              <Button
                size="$2"
                onPress={() => onImportSchema(connection._id)}
                backgroundColor={theme ? '#10b981' : '#059669'}
                color="white"
                flex={1}
              >
                <SizableText color="white" size="$2">
                  Import
                </SizableText>
              </Button>
            )}
            
            {onEdit && (
              <Button
                size="$2"
                onPress={() => onEdit(connection)}
                backgroundColor={theme ? '#6b7280' : '#4b5563'}
                color="white"
                flex={1}
              >
                <SizableText color="white" size="$2">
                  Edit
                </SizableText>
              </Button>
            )}
            
            {onDelete && (
              <Button
                size="$2"
                onPress={handleDelete}
                backgroundColor={theme ? '#ef4444' : '#dc2626'}
                color="white"
                flex={1}
              >
                <SizableText color="white" size="$2">
                  Delete
                </SizableText>
              </Button>
            )}
          </XStack>
        </YStack>
      </Pressable>
    </Animatable.View>
  );
};

export default DatabaseConnectionCard;



